<template>
  <div :style="backgroundStyle" class="random-color" @click="generateColor">
    <ToolHeader title="Random Color" />
    <div class="random-color__color" @click.stop>
      <template v-if="isDefaultColor">
        Click to generate
      </template>
      <template v-else>#{{ randomColor }}</template>
    </div>
    <div
      v-if="!isDefaultColor && colorList.length"
      class="random-color__stack"
      @click.stop
    >
      <div
        v-for="color in colorList"
        :key="color"
        :style="{ backgroundColor: `#${color}` }"
        class="random-color__stack-item"
      />
    </div>
  </div>
</template>

<script>
import { computed, ref, reactive } from "vue";
import { useRoute, useRouter } from "vue-router";

// Utilities
import config from "@config";
import { getContrastText, parseHexColor } from "@utils/colors";

const hexChars = "ABCDEF0123456789";
const COLOR_LIST_SIZE = 5;

// NOTE: Routing is used to determine color string

export default {
  name: "RandomColor",
  setup() {
    const route = useRoute();
    const router = useRouter();

    // NOTE: 'useRef' is for primitives, 'useReactive' is for objects
    const randomColor = ref(null);
    const colorHistory = reactive([]);

    const isDefaultColor = computed(() => !route.params.color);

    // Only the last few colors should display in "quick access" bar
    const colorList = computed(() => {
      let history = colorHistory;
      if (!history || !history.length) return [];

      // QUESTION: Hide current color?
      history = history.slice(0, -1);

      // Hide extra past colors
      return history.length > COLOR_LIST_SIZE
        ? history.slice(-COLOR_LIST_SIZE)
        : history;
    });

    /**
     * Set the current color
     * @param {string} color - New hex color
     */
    const setColor = (color) => {
      randomColor.value = color;

      // Selecting an existing color should only happen when navigating backwards,
      //  which should reset the stack to that point in time (dropping future colors).
      const existingIndex = colorHistory.indexOf(color);
      if (existingIndex >= 0) {
        colorHistory.splice(existingIndex);
      }

      colorHistory.push(color);
    };

    // Set the current random color when loading from URL
    const colorString = route.params.color || config.colors.default.slice(1);
    setColor(colorString);

    const backgroundStyle = computed(() => {
      const backgroundColor = randomColor.value
        ? `#${randomColor.value}`
        : config.colors.default;
      const color = getContrastText(backgroundColor);

      return {
        backgroundColor,
        color,
      };
    });

    /**
     * Generate a random hex color (and update route)
     * @return {string} - Random hex color
     */
    const generateColor = () => {
      let colorString = "";

      for (let i = 0; i < 6; i++) {
        colorString += hexChars[Math.floor(Math.random() * hexChars.length)];
      }

      router.push({ params: { color: colorString } });
    };

    return {
      // Data
      colorList,
      randomColor,
      // Computed
      backgroundStyle,
      isDefaultColor,
      // Methods
      generateColor,
      setColor,
    };
  },
  beforeRouteEnter(to, from, next) {
    // NOTE: Only worry about redirecting invalid colors (color is set in 'setup')

    const { color: colorString = null } = to.params;
    const color = parseHexColor(colorString);

    // Redirect to default IF an invalid color was provided
    return !color && colorString ? next({ name: "randomColor" }) : next();
  },
  beforeRouteUpdate(to, from, next) {
    const { color: colorString = null } = to.params;
    const color = parseHexColor(colorString);

    // Redirect to default IF an invalid color was provided
    if (!color && colorString) return next({ name: "randomColor" });

    this.setColor(colorString);

    return next();
  },
};
</script>

<style lang="scss" scoped>
.random-color {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  user-select: none;

  transition: background-color 0.2s ease;
}

// Random color string
.random-color__color {
  display: flex;
  flex-direction: column;
  padding: 32px;
  color: inherit;
  font-size: 300%;
  font-weight: 200;
  user-select: all;
}

// List of recent random colors
.random-color__stack {
  position: fixed;
  bottom: 32px;
  display: flex;
  align-items: center;
  padding: 16px;

  > div:not(:first-child) {
    margin-left: 16px;
  }

  // Indicate the current color
  /*> div:last-child {
    margin-left: 24px;
    transform: scale(1.25);
  }*/
}

.random-color__stack-item {
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: 2px solid white;
  cursor: pointer;

  @include elevate(4);
}
</style>
